// ============================================
// 📊 SISTEMA DE LOGS - Auditoria
// ============================================
// Registra todas as ações dos usuários
// Salve em: src/utils/auditoria.js
// ============================================

// MULTI-TENANT: inclui tenant_id em todos os logs
import supabase, { getCurrentTenantId } from '../services/supabaseClient';

// Lazy: evita import circular e garante que o store já foi inicializado
let _getStore = null;
const getStore = () => {
  if (!_getStore) {
    // eslint-disable-next-line
    _getStore = require('../store/useAppStore').default;
  }
  return _getStore;
};

/**
 * Registra uma ação no log de auditoria
 * @param {Object} params - Parâmetros do log
 * @param {string} params.acao - Tipo de ação (login, criar_cliente, editar_usuario, etc)
 * @param {string} params.tabela - Nome da tabela afetada (opcional)
 * @param {string} params.registro_id - ID do registro afetado (opcional)
 * @param {Object} params.detalhes - Informações adicionais em JSON (opcional)
 */
export const registrarLog = async ({ acao, tabela = null, registro_id = null, detalhes = null, tenantId: tenantIdExplicito = undefined }) => {
  try {
    // FASE 3: lê usuário do Zustand store (fonte de verdade após migração para Supabase Auth)
    const usuario = getStore().getState().usuario;
    if (!usuario) {
      console.warn('Tentou registrar log sem usuário logado');
      return;
    }

    // MULTI-TENANT: tenantIdExplicito tem prioridade (evita race condition no logLogin)
    // Fallback para o store quando não é passado explicitamente
    const tenantId = tenantIdExplicito !== undefined ? tenantIdExplicito : getCurrentTenantId();

    // Prepara dados do log
    const logData = {
      usuario_id: usuario.id,
      usuario_nome: usuario.nome,
      usuario_email: usuario.email,
      usuario_perfil: usuario.perfil,
      acao,
      tabela,
      registro_id: registro_id ? String(registro_id) : null,
      detalhes: detalhes ? detalhes : null,
      tenant_id: tenantId || null,
      criado_em: new Date().toISOString()
    };

    // Usa o cliente autenticado para que o JWT da sessão passe pelo RLS
    const { error } = await supabase.from('logs_auditoria').insert(logData);
    if (error) {
      console.error('Erro ao registrar log:', error);
    }
  } catch (err) {
    console.error('Erro ao registrar log:', err);
  }
};

// ============================================
// 🎯 FUNÇÕES ESPECÍFICAS DE LOG
// ============================================

// AUTENTICAÇÃO
export const logLogin = (tenantId) => registrarLog({ acao: 'login', tenantId });
export const logLogout = () => registrarLog({ acao: 'logout' });
export const logCadastro = (email) => registrarLog({ acao: 'cadastro', detalhes: { email } });

// USUÁRIOS
export const logCriarUsuario = (usuarioId, dados) => 
  registrarLog({ 
    acao: 'criar_usuario', 
    tabela: 'usuarios', 
    registro_id: usuarioId,
    detalhes: dados 
  });

export const logEditarUsuario = (usuarioId, dadosAntigos, dadosNovos) => 
  registrarLog({ 
    acao: 'editar_usuario', 
    tabela: 'usuarios', 
    registro_id: usuarioId,
    detalhes: { antes: dadosAntigos, depois: dadosNovos }
  });

export const logExcluirUsuario = (usuarioId, nome) => 
  registrarLog({ 
    acao: 'excluir_usuario', 
    tabela: 'usuarios', 
    registro_id: usuarioId,
    detalhes: { nome }
  });

export const logAprovarUsuario = (usuarioId, nome) => 
  registrarLog({ 
    acao: 'aprovar_usuario', 
    tabela: 'usuarios', 
    registro_id: usuarioId,
    detalhes: { nome }
  });

export const logDesativarUsuario = (usuarioId, nome) => 
  registrarLog({ 
    acao: 'desativar_usuario', 
    tabela: 'usuarios', 
    registro_id: usuarioId,
    detalhes: { nome }
  });

export const logResetarSenha = (usuarioId, nome) => 
  registrarLog({ 
    acao: 'resetar_senha', 
    tabela: 'usuarios', 
    registro_id: usuarioId,
    detalhes: { nome }
  });

export const logAlterarPerfil = (usuarioId, nome, perfilAntigo, perfilNovo) => 
  registrarLog({ 
    acao: 'alterar_perfil', 
    tabela: 'usuarios', 
    registro_id: usuarioId,
    detalhes: { nome, de: perfilAntigo, para: perfilNovo }
  });

// CLIENTES
export const logCriarCliente = (clienteId, dados) => 
  registrarLog({ 
    acao: 'criar_cliente', 
    tabela: 'clientes', 
    registro_id: clienteId,
    detalhes: dados 
  });

export const logEditarCliente = (clienteId, dadosAntigos, dadosNovos) => 
  registrarLog({ 
    acao: 'editar_cliente', 
    tabela: 'clientes', 
    registro_id: clienteId,
    detalhes: { antes: dadosAntigos, depois: dadosNovos }
  });

export const logExcluirCliente = (clienteId, nome) => 
  registrarLog({ 
    acao: 'excluir_cliente', 
    tabela: 'clientes', 
    registro_id: clienteId,
    detalhes: { nome }
  });

export const logAdicionarObservacao = (clienteId, observacao) => 
  registrarLog({ 
    acao: 'adicionar_observacao', 
    tabela: 'clientes', 
    registro_id: clienteId,
    detalhes: { observacao }
  });

export const logExcluirObservacao = (clienteId, observacaoId) => 
  registrarLog({ 
    acao: 'excluir_observacao', 
    tabela: 'clientes', 
    registro_id: clienteId,
    detalhes: { observacao_id: observacaoId }
  });

// FINANCEIRO
export const logCriarVenda = (vendaId, dados) => 
  registrarLog({ 
    acao: 'criar_venda', 
    tabela: 'vendas', 
    registro_id: vendaId,
    detalhes: dados 
  });

export const logEditarVenda = (vendaId, dadosAntigos, dadosNovos) => 
  registrarLog({ 
    acao: 'editar_venda', 
    tabela: 'vendas', 
    registro_id: vendaId,
    detalhes: { antes: dadosAntigos, depois: dadosNovos }
  });

export const logExcluirVenda = (vendaId, dados) => 
  registrarLog({ 
    acao: 'excluir_venda', 
    tabela: 'vendas', 
    registro_id: vendaId,
    detalhes: dados 
  });

export const logCriarDespesa = (despesaId, dados) => 
  registrarLog({ 
    acao: 'criar_despesa', 
    tabela: 'despesas', 
    registro_id: despesaId,
    detalhes: dados 
  });

export const logEditarDespesa = (despesaId, dadosAntigos, dadosNovos) => 
  registrarLog({ 
    acao: 'editar_despesa', 
    tabela: 'despesas', 
    registro_id: despesaId,
    detalhes: { antes: dadosAntigos, depois: dadosNovos }
  });

export const logExcluirDespesa = (despesaId, dados) => 
  registrarLog({ 
    acao: 'excluir_despesa', 
    tabela: 'despesas', 
    registro_id: despesaId,
    detalhes: dados 
  });

// DISPAROS
export const logEnviarMensagem = (clienteId, tipo) => 
  registrarLog({ 
    acao: 'enviar_mensagem', 
    tabela: 'disparos', 
    registro_id: clienteId,
    detalhes: { tipo }
  });

export const logCriarDisparo = (disparoId, dados) => 
  registrarLog({ 
    acao: 'criar_disparo', 
    tabela: 'disparos', 
    registro_id: disparoId,
    detalhes: dados 
  });

// CONFIGURAÇÕES
export const logEditarConfigIA = (configAntiga, configNova) => 
  registrarLog({ 
    acao: 'editar_config_ia', 
    tabela: 'config_ia',
    detalhes: { antes: configAntiga, depois: configNova }
  });

export const logEditarConfigDisparo = (grupo, configAntiga, configNova) => 
  registrarLog({ 
    acao: 'editar_config_disparo', 
    tabela: 'config_disparo',
    detalhes: { grupo, antes: configAntiga, depois: configNova }
  });

export const logImportarDados = (arquivo, quantidadeRegistros) =>
  registrarLog({
    acao: 'importar_dados',
    detalhes: { arquivo, quantidade: quantidadeRegistros }
  });

// CRM — BLOQUEIO
export const logBloqueioCliente = (telefone, bloqueado) =>
  registrarLog({
    acao: bloqueado ? 'bloquear_cliente' : 'desbloquear_cliente',
    tabela: 'clientes',
    registro_id: telefone,
    detalhes: { telefone, bloqueado_permanente: bloqueado }
  });

// CAIXA — FECHAMENTO/REABERTURA
export const logFecharCaixa = (fechamentoId, dados) =>
  registrarLog({
    acao: 'fechar_caixa',
    tabela: 'caixa_fechamentos',
    registro_id: fechamentoId,
    detalhes: dados
  });

export const logReabrirCaixa = (fechamentoId, motivo, dados) =>
  registrarLog({
    acao: 'reabrir_caixa',
    tabela: 'caixa_fechamentos',
    registro_id: fechamentoId,
    detalhes: { motivo, ...dados }
  });
